import { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";

// ───────── Constants ─────────
const TYPE_OPTIONS = [
  { value: "location", label: "Investissement locatif", icon: "🏠" },
  { value: "residence_principale", label: "Résidence principale", icon: "🏡" },
  { value: "defiscalisation", label: "Défiscalisation", icon: "📜" },
  { value: "mixte", label: "Mixte / Hybride", icon: "🧭" },
  { value: "autre", label: "Autre", icon: "✦" },
];

const HORIZON_OPTIONS = [
  { value: "court", label: "Court terme", help: "< 2 ans" },
  { value: "moyen", label: "Moyen terme", help: "2 – 5 ans" },
  { value: "long", label: "Long terme", help: "> 5 ans" },
];

const TYPE_MAP = Object.fromEntries(TYPE_OPTIONS.map(o => [o.value, o]));
const HORIZON_MAP = Object.fromEntries(HORIZON_OPTIONS.map(o => [o.value, o]));

const formatAmount = (n) => {
  if (n == null || n === "") return null;
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `${n} €`;
  }
};

/**
 * Investment Objective card — editable by student (their own), coach (assigned)
 * and admin. Read-only display when `readOnly` is true.
 */
export default function InvestmentObjectiveCard({ studentId, readOnly = false, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ type: "", amount: "", horizon: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await api.get(`/investment-objective/${studentId}`);
      setData(res.data);
      setForm({
        type: res.data?.type || "",
        amount: res.data?.amount != null ? String(res.data.amount) : "",
        horizon: res.data?.horizon || "",
        notes: res.data?.notes || "",
      });
    } catch (e) {
      console.error("Error loading objective:", e);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (readOnly) return;
    setSaving(true);
    try {
      const payload = {
        type: form.type || null,
        amount: form.amount !== "" ? Number(form.amount) : null,
        horizon: form.horizon || null,
        notes: form.notes || "",
      };
      const res = await api.put(`/investment-objective/${studentId}`, payload);
      setData(res.data);
      setEditing(false);
      toast({ title: "Objectif d'investissement enregistré" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err?.response?.data?.detail || "Impossible d'enregistrer",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      type: data?.type || "",
      amount: data?.amount != null ? String(data.amount) : "",
      horizon: data?.horizon || "",
      notes: data?.notes || "",
    });
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-5" data-testid="investment-objective-card">
        <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-64 bg-gray-100 rounded mt-3 animate-pulse" />
      </div>
    );
  }

  const hasData = data?.exists && (data?.type || data?.amount != null || data?.horizon);
  const typeOpt = data?.type ? TYPE_MAP[data.type] : null;
  const horizonOpt = data?.horizon ? HORIZON_MAP[data.horizon] : null;

  // ───────── EDIT MODE ─────────
  if (editing && !readOnly) {
    return (
      <form
        onSubmit={handleSave}
        className="glass-card rounded-2xl p-5 space-y-4"
        data-testid="investment-objective-card-edit"
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="icon-circle icon-circle--gold">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ fontFamily: "var(--serif)", color: "var(--ink)" }}>
                Objectif d'investissement
              </h3>
              <p className="text-xs italic" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
                Type · budget · horizon
              </p>
            </div>
          </div>
        </div>

        {/* Type */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>
            Type d'objectif
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                data-testid={`objective-type-${opt.value}`}
                onClick={() => setForm({ ...form, type: opt.value })}
                className={`text-left p-2.5 rounded-xl border transition-all text-xs sm:text-sm ${
                  form.type === opt.value
                    ? "border-[color:var(--gold-dark)] bg-[color:var(--paper-2)] font-bold"
                    : "border-[color:var(--line)] hover:border-[color:var(--gold-soft)] bg-white"
                }`}
                style={{ color: "var(--ink)" }}
              >
                <div className="text-base mb-0.5">{opt.icon}</div>
                <div>{opt.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Amount + Horizon */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>
              Budget cible (€)
            </label>
            <input
              type="number"
              min="0"
              step="1000"
              placeholder="Ex. 180000"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              className="input-dark w-full"
              data-testid="objective-amount-input"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>
              Horizon
            </label>
            <div className="flex gap-2">
              {HORIZON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`objective-horizon-${opt.value}`}
                  onClick={() => setForm({ ...form, horizon: opt.value })}
                  className={`flex-1 px-2 py-2 rounded-xl border transition-all text-xs ${
                    form.horizon === opt.value
                      ? "border-[color:var(--gold-dark)] bg-[color:var(--paper-2)] font-bold"
                      : "border-[color:var(--line)] hover:border-[color:var(--gold-soft)] bg-white"
                  }`}
                  style={{ color: "var(--ink)" }}
                >
                  <div>{opt.label}</div>
                  <div className="text-[10px] italic mt-0.5" style={{ color: "var(--muted)" }}>{opt.help}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider block mb-2" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>
            Notes (zone géo, contraintes, ambitions…)
          </label>
          <textarea
            rows={3}
            placeholder="Studio Paris/Lyon, rendement >5%, pas de gros travaux…"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            className="input-dark w-full resize-y"
            style={{ fontFamily: "var(--serif)", fontStyle: "italic" }}
            data-testid="objective-notes-textarea"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-ghost"
            disabled={saving}
            data-testid="objective-cancel-btn"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving}
            data-testid="objective-save-btn"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    );
  }

  // ───────── DISPLAY MODE ─────────
  return (
    <div className="glass-card rounded-2xl p-5" data-testid="investment-objective-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="icon-circle icon-circle--gold">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ fontFamily: "var(--serif)", color: "var(--ink)" }}>
              Objectif d'investissement
            </h3>
            <p className="text-xs italic" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
              {hasData
                ? `Mis à jour ${data.updated_by_role === "coach" ? "par le coach" : data.updated_by_role === "admin" ? "par l'admin" : "par l'élève"}`
                : "Non renseigné"}
            </p>
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors"
            style={{
              color: "var(--gold-dark)",
              letterSpacing: "0.08em",
              background: "var(--paper-2)",
              border: "1px solid var(--gold-soft)",
            }}
            data-testid="objective-edit-btn"
          >
            {hasData ? "Modifier" : "Renseigner"}
          </button>
        )}
      </div>

      {!hasData ? (
        <p className="text-sm italic text-center py-4" style={{ color: "var(--muted)", fontFamily: "var(--serif)" }}>
          {readOnly
            ? "L'élève n'a pas encore défini son objectif."
            : "Définis ton objectif pour aider ton coach à mieux te guider."}
        </p>
      ) : (
        <div className={`grid grid-cols-1 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-3"} gap-3`}>
          {typeOpt && (
            <div className="p-3 rounded-xl border" style={{ borderColor: "var(--gold-soft)", background: "var(--paper-2)" }}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>Type</div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{typeOpt.icon}</span>
                <span className="text-sm font-bold" style={{ color: "var(--ink)" }} data-testid="objective-display-type">{typeOpt.label}</span>
              </div>
            </div>
          )}
          {data.amount != null && (
            <div className="p-3 rounded-xl border" style={{ borderColor: "var(--gold-soft)", background: "var(--paper-2)" }}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>Budget cible</div>
              <div className="text-base font-bold" style={{ color: "var(--ink)", fontFamily: "var(--serif)" }} data-testid="objective-display-amount">
                {formatAmount(data.amount)}
              </div>
            </div>
          )}
          {horizonOpt && (
            <div className="p-3 rounded-xl border" style={{ borderColor: "var(--gold-soft)", background: "var(--paper-2)" }}>
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>Horizon</div>
              <div className="text-sm font-bold" style={{ color: "var(--ink)" }} data-testid="objective-display-horizon">{horizonOpt.label}</div>
              <div className="text-[11px] italic" style={{ color: "var(--muted)" }}>{horizonOpt.help}</div>
            </div>
          )}
        </div>
      )}

      {hasData && data.notes && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--line)" }}>
          <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--gold-dark)", letterSpacing: "0.08em" }}>Notes</div>
          <p className="text-sm italic whitespace-pre-wrap" style={{ color: "var(--ink-soft)", fontFamily: "var(--serif)" }} data-testid="objective-display-notes">
            {data.notes}
          </p>
        </div>
      )}
    </div>
  );
}
